// Decides, for a doctor_visit in status "PR" (Awaiting Patient Reply), which
// message thread the patient's action card should link to and whether the
// patient has already replied.
//
// A patient has one care thread and one doctor thread, so this is computed once
// at the patient level and applied to every "PR" card.
//
// Authorship rule (matches portalHeader.jsx and the message-thread components):
// a message is from staff (care team / doctor) iff `emrUserFirstName` is set;
// an empty `emrUserFirstName` means the patient authored it.
//
// Priority (Care preferred — see docs/patient-portal-treatment-card.md):
//   1. Care thread awaiting reply (latest care msg from staff)   -> care,   not replied
//   2. else doctor thread awaiting reply (latest doctor from staff) -> doctor, not replied
//   3. else neither awaiting (patient's msg is latest, or no staff msgs):
//        patientHasReplied = a message exists; link to the active thread (care preferred).

const fromStaff = (msg) => !!msg && !!msg.emrUserFirstName;

const latestMessage = (messages) =>
  (messages || []).reduce(
    (latest, msg) =>
      latest == null || new Date(msg.createdAt) >= new Date(latest.createdAt)
        ? msg
        : latest,
    null,
  );

// Returns { replyThread: "care" | "doctor", patientHasReplied: boolean }.
export function computeMessageReplyState(careMessages, doctorMessages) {
  const lastCare = latestMessage(careMessages);
  const lastDoctor = latestMessage(doctorMessages);

  if (fromStaff(lastCare)) {
    return { replyThread: "care", patientHasReplied: false };
  }
  if (fromStaff(lastDoctor)) {
    return { replyThread: "doctor", patientHasReplied: false };
  }

  // Neither thread is awaiting our reply: the patient's message is the latest in
  // whichever threads have messages (or there are no messages at all).
  const patientHasReplied = !!lastCare || !!lastDoctor;
  const replyThread = lastCare ? "care" : lastDoctor ? "doctor" : "care";
  return { replyThread, patientHasReplied };
}
